import {
  FilteredGuest,
  SelectionGroup,
  Selections,
  SpaPackageBasketItem,
  AvailabilityForm,
} from '@features/DynamicPackagesConfigurationWizard/types';
import { PackageType } from '@features/DynamicPackagesConfigurationWizard/types/PackageType';
import moment from 'moment';
import { OvernightPackageSpaExtras } from '../types/SpaExtra.schema';
import { MappedLoggedGuestData } from './addToBasket';
import {
  filterIncompleteGuests,
  isCompleteGuest,
  isGuestInFilteredList,
  parseGuestDetails,
} from './guest.helpers';
import { pickTimeBand, SpaTimeBand } from './spaTimeBands';

export function mapSpaExtrasToSelectionGroups(
  spaExtras: OvernightPackageSpaExtras,
  filteredGuests: FilteredGuest[]
): SelectionGroup[] {
  const selections = (spaExtras || []).reduce((selections, extra) => {
    extra.selectedGuestDetails.forEach((guest) => {
      if (isGuestInFilteredList(filteredGuests, guest)) {
        const matchingFilteredGuest = filteredGuests.find(
          (g) =>
            g.firstName === guest.firstName &&
            g.lastName === guest.lastName &&
            g.title === guest.title
        );
        selections.push({
          extra: extra.extra,
          guest_details: parseGuestDetails(matchingFilteredGuest),
        });
      }
    });
    return selections;
  }, [] as Selections);
  return [{ is_extras: true, selections }];
}

function mapSpaExperiencesToConfigGroups(
  guestExperiences: (AvailabilityForm | null | undefined)[][],
  filteredGuests: FilteredGuest[]
): SelectionGroup[] {
  const selectionGroups: SelectionGroup[] = [];
  const addedGroupIds = new Set<number | string>();

  const isBookingFilled = (booking: any) =>
    booking &&
    booking.startTime &&
    booking.endTime &&
    booking.experienceId &&
    booking.optionId !== undefined;

  guestExperiences.forEach((guestExp) => {
    (guestExp || []).forEach((booking) => {
      const optionGroupId = booking?.optionGroupId;
      if (optionGroupId && !addedGroupIds.has(optionGroupId)) {
        selectionGroups.push({
          configuration_group: { id: Number(optionGroupId) },
          selections: [],
        });
        addedGroupIds.add(optionGroupId);
      }
    });
  });

  guestExperiences.forEach((guestExp, guestIndex) => {
    const filteredGuest = filteredGuests[guestIndex];
    if (!filteredGuest) return;

    (guestExp || []).forEach((booking) => {
      if (!isBookingFilled(booking) || !booking) return;

      const { optionGroupId, optionId, startTime, endTime, visitDate } = booking;
      const selectionGroup = selectionGroups.find(
        (grp) => Number(grp.configuration_group?.id) === Number(optionGroupId)
      );

      if (!selectionGroup) return;

      selectionGroup.selections.push({
        option: { id: Number(optionId) },
        start_time: startTime,
        end_time: endTime,
        guest_details: parseGuestDetails(filteredGuest),
        start_date: moment(visitDate).format('YYYY-MM-DD'),
        end_date: moment(visitDate).format('YYYY-MM-DD'),
      });
    });
  });

  return selectionGroups;
}

function mapRepeatableSpaExperiencesByRepetition(
  guestExperiences: (AvailabilityForm | null | undefined)[][],
  filteredGuests: FilteredGuest[],
  numberOfNights: number
): SelectionGroup[][] {
  if (numberOfNights <= 0) return [];

  const slotsForGuestGroup = (guestExp: (AvailabilityForm | null | undefined)[]) => {
    const byGroup = new Map<number, AvailabilityForm[]>();
    guestExp.forEach((booking) => {
      if (!booking?.optionGroupId) return;
      const id = Number(booking.optionGroupId);
      if (!byGroup.has(id)) byGroup.set(id, []);
      byGroup.get(id)!.push(booking);
    });
    return byGroup;
  };

  const repetitions: (AvailabilityForm | null | undefined)[][][] = Array.from(
    { length: numberOfNights },
    () => guestExperiences.map(() => [])
  );

  guestExperiences.forEach((guestExp, guestIndex) => {
    const byGroup = slotsForGuestGroup(guestExp || []);
    byGroup.forEach((bookings) => {
      const perRepetition = Math.max(1, Math.floor(bookings.length / numberOfNights));
      bookings.forEach((booking, idx) => {
        const repetitionIndex = Math.min(numberOfNights - 1, Math.floor(idx / perRepetition));
        repetitions[repetitionIndex][guestIndex].push(booking);
      });
    });
  });

  return repetitions
    .map((repetitionGuestExp) =>
      mapSpaExperiencesToConfigGroups(repetitionGuestExp, filteredGuests)
    )
    .filter((groups) => groups.some((g) => g.selections.length > 0));
}

function mapRepeatableSpaExperiencesByInstance(
  guestExperiences: (AvailabilityForm | null | undefined)[][],
  filteredGuests: FilteredGuest[],
  fromDate: string,
  numberOfNights: number,
  experienceDayOffsetsByGroupId: Record<string, number>,
  clampToStay: boolean
): SelectionGroup[][] {
  const instanceIndexFor = (booking: AvailabilityForm | null | undefined): number | null => {
    if (!booking?.visitDate) return null;
    const occurrenceDay = moment(booking.visitDate).diff(moment(fromDate), 'days');
    const dayOffset = experienceDayOffsetsByGroupId[String(booking.optionGroupId)] || 0;
    const rawIndex = Math.max(0, occurrenceDay - dayOffset);
    return clampToStay ? Math.min(rawIndex, Math.max(0, numberOfNights - 1)) : rawIndex;
  };

  const instanceIndexes = new Set<number>();
  guestExperiences.forEach((guestExp) => {
    (guestExp || []).forEach((booking) => {
      const instanceIndex = instanceIndexFor(booking);
      if (instanceIndex !== null) instanceIndexes.add(instanceIndex);
    });
  });

  return [...instanceIndexes]
    .sort((a, b) => a - b)
    .map((instanceIndex) => {
      const experiencesForInstance = guestExperiences.map((guestExp) =>
        (guestExp || []).filter((booking) => instanceIndexFor(booking) === instanceIndex)
      );

      return mapSpaExperiencesToConfigGroups(experiencesForInstance, filteredGuests);
    })
    .filter((groups) => groups.some((g) => g.selections.length > 0));
}

export function parseSpaExperiencesForBasket(
  guestDetails: any[],
  guestExperiences: (AvailabilityForm | null | undefined)[][],
  spaExtras: OvernightPackageSpaExtras,
  spaPackageId: number | string,
  fromDate: string,
  toDate: string,
  arrivalTime: string,
  endTime: string,
  maxGuestsPerExperience: number,
  loggedInUserGuestData: MappedLoggedGuestData | null,
  packageType: PackageType,
  parentBasketId?: number,
  isRepeatedFlexibleEnabled = false,
  stayDuration?: number,
  experienceDayOffsetsByGroupId: Record<string, number> = {},
  note?: string,
  timeBandsByDate: Record<string, SpaTimeBand[] | undefined> = {}
): SpaPackageBasketItem[] {
  const filteredGuests = filterIncompleteGuests(guestDetails, loggedInUserGuestData);
  const filteredGuestExperiences = guestDetails.flatMap((guest, index) =>
    isCompleteGuest(guest) ? [guestExperiences[index] ?? []] : []
  );
  const dateDiff = moment(toDate).diff(moment(fromDate), 'days');
  if (dateDiff < 0) {
    throw new Error(
      `parseSpaExperiencesForBasket: toDate (${toDate}) is before fromDate (${fromDate})`
    );
  }
  if (isRepeatedFlexibleEnabled && packageType === PackageType.REPEATING && !stayDuration) {
    throw new Error(
      'parseSpaExperiencesForBasket: stayDuration is required for REPEATING packages when feature.package-repeated-flexible is enabled'
    );
  }
  const numberOfNights = stayDuration && stayDuration > 0 ? stayDuration : Math.max(1, dateDiff);

  const groupRepeating = (
    exp: (AvailabilityForm | null | undefined)[][],
    guests: FilteredGuest[]
  ) =>
    isRepeatedFlexibleEnabled
      ? mapRepeatableSpaExperiencesByRepetition(exp, guests, numberOfNights)
      : mapRepeatableSpaExperiencesByInstance(
          exp,
          guests,
          fromDate,
          numberOfNights,
          experienceDayOffsetsByGroupId,
          isRepeatedFlexibleEnabled
        );

  const earliestSelectionDate = (grp: SelectionGroup[]): string | undefined => {
    let earliest: string | undefined;
    grp.forEach((group) => {
      group.selections.forEach((sel) => {
        if ('start_date' in sel && sel.start_date) {
          if (!earliest || sel.start_date < earliest) earliest = sel.start_date;
        }
      });
    });
    return earliest;
  };

  const stayTimes = (grp: SelectionGroup[], checkInDate: string, checkOutDate: string) => {
    const band = pickTimeBand(
      timeBandsByDate[checkInDate] || [],
      checkInDate,
      checkOutDate,
      grp.flatMap((group) =>
        group.selections.flatMap((selection) => ('start_time' in selection ? [selection] : []))
      )
    );

    return {
      check_in: `${checkInDate}  ${band?.startTime ?? arrivalTime}`,
      check_out: `${checkOutDate}  ${band?.endTime ?? endTime}`,
    };
  };

  const partySelectionGroups =
    packageType === PackageType.REPEATING
      ? groupRepeating(filteredGuestExperiences, filteredGuests)
      : [mapSpaExperiencesToConfigGroups(filteredGuestExperiences, filteredGuests)];

  const partyItemDates = (grp: SelectionGroup[]) => {
    const firstSelection = grp[0]?.selections?.[0];
    const fallbackDate =
      firstSelection && 'start_date' in firstSelection && firstSelection.start_date
        ? firstSelection.start_date
        : moment(fromDate).format('YYYY-MM-DD');
    const visitDateFromSelection = earliestSelectionDate(grp) ?? fallbackDate;

    const hasMultipleDays = grp.some((group) =>
      group.selections.some(
        (sel) => 'start_date' in sel && sel.start_date !== visitDateFromSelection
      )
    );
    const isRepeatableAndAllOnSameDay = packageType === PackageType.REPEATING && !hasMultipleDays;

    const checkOutDate = isRepeatableAndAllOnSameDay
      ? visitDateFromSelection
      : packageType === PackageType.REPEATING
      ? moment(visitDateFromSelection).add(1, 'day').format('YYYY-MM-DD')
      : moment(toDate).format('YYYY-MM-DD');

    return { visitDateFromSelection, checkOutDate };
  };

  const noBandHoldsTheParty = partySelectionGroups.some((grp) => {
    const { visitDateFromSelection, checkOutDate } = partyItemDates(grp);
    const bands = timeBandsByDate[visitDateFromSelection] || [];

    return (
      bands.length > 0 &&
      !pickTimeBand(
        bands,
        visitDateFromSelection,
        checkOutDate,
        grp.flatMap((group) =>
          group.selections.flatMap((selection) => ('start_time' in selection ? [selection] : []))
        )
      )
    );
  });

  if (filteredGuests.length > maxGuestsPerExperience || noBandHoldsTheParty) {
    // Create separate basket items for each guest
    return filteredGuests
      .map((guest, index) => {
        const currentGuestExperiences = filteredGuestExperiences[index] || [];

        const mappedSelectionGroups =
          packageType === PackageType.REPEATING
            ? groupRepeating([currentGuestExperiences], [guest])
            : [mapSpaExperiencesToConfigGroups([currentGuestExperiences], [guest])];

        const mappedExtraSelectionGroups = mapSpaExtrasToSelectionGroups(spaExtras, [guest]);
        return mappedSelectionGroups
          .map((grp) => {
            const firstSelection = grp[0]?.selections?.[0];
            const fallbackDate =
              firstSelection && 'start_date' in firstSelection && firstSelection.start_date
                ? firstSelection.start_date
                : moment(fromDate).format('YYYY-MM-DD');
            const visitDateFromSelection = earliestSelectionDate(grp) ?? fallbackDate;

            const isRepeatableAndAllOnSameDay =
              packageType === PackageType.REPEATING &&
              grp.every((group) =>
                group.selections.every(
                  (sel) => 'start_date' in sel && sel.start_date === visitDateFromSelection
                )
              );

            const checkOutDate = isRepeatableAndAllOnSameDay
              ? visitDateFromSelection
              : packageType === PackageType.REPEATING
              ? moment(visitDateFromSelection).add(1, 'day').format('YYYY-MM-DD')
              : moment(toDate).format('YYYY-MM-DD');

            return {
              type: 'spa-packages',
              spa_package: {
                id: Number(spaPackageId),
              },
              ...stayTimes(grp, visitDateFromSelection, checkOutDate),
              guest_details: [parseGuestDetails(guest)],
              dynamic_package: 'child',
              package_basket_item_id: parentBasketId,
              ...(note ? { note } : {}),
              selection_groups: [...grp, ...mappedExtraSelectionGroups],
            };
          })
          .flat();
      })
      .flat();
  }

  const mappedExtraSelectionGroups = mapSpaExtrasToSelectionGroups(spaExtras, filteredGuests);

  return partySelectionGroups
    .map((grp) => {
      const { visitDateFromSelection, checkOutDate } = partyItemDates(grp);

      return {
        type: 'spa-packages',
        spa_package: {
          id: Number(spaPackageId),
        },
        ...stayTimes(grp, visitDateFromSelection, checkOutDate),
        guest_details: filteredGuests.map(parseGuestDetails),
        dynamic_package: 'child',
        ...(parentBasketId ? { package_basket_item_id: parentBasketId } : {}),
        ...(note ? { note } : {}),
        selection_groups: [...grp, ...mappedExtraSelectionGroups],
      };
    })
    .flat();
}
